<template>
  <div class="document-uploader fake-section">
    <input class="file-input" ref="fileInput" type="file" name="file" id="file" accept=".jpg, .jpeg, .png" @change="startUpload">

    <div v-if="app.deviceIsDesktop" class="desktop-uploader">
      <div class="header">
        <div class="info-image" :class="{ big: screenParams.infoImageBig }" :style="{ backgroundImage: `url(${screenParams.infoImage})` }"></div>
        <h1>{{ i18n.t('specifics:documentUploader.header', { type: idTypeCopy }) }}<span v-if="screenParams.infoModalText" class="info-btn" @click="openInfoModal"></span></h1>
        <h5>{{ i18n.t('specifics:documentUploader.info', { type: idTypeCopy }) }}</h5>
      </div>
      
      <div ref="dropZone" class="drop-zone">
        <div v-if="!uploading" class="info">
          <div class="info-image" :class="{ big: screenParams.infoImageBig }" :style="{ backgroundImage: `url(${screenParams.infoImage})` }"></div>
          <small class="info-description">{{ i18n.t('specifics:documentUploader.drag_file') }}</small>
        </div>
        <div v-else class="upload-info">
          <p class="upload-info-percentage">{{done ? i18n.t('specifics:documentUploader.done') : `${progress}%`}}</p>
          <p class="upload-info-filename">{{ filename }}</p>
          <p class="upload-info-sizeuploaded">
            <span v-if="!done">{{`${normalizedUploadedSize} ${i18n.t('specifics:documentUploader.from')}`}}</span> {{`${normalizedFileSize} ${i18n.t('specifics:documentUploader.uploaded')}`}}
          </p>
        </div>

        <button @click="openUpload" :class="{ done, loading: uploading }">
          <div v-if="uploading" class="progress" :style="`width:${100 - progress}%;`"/>
          <span>{{ buttonText }}</span>
        </button>
      </div>

      <small class="disclaimer">{{ i18n.t('specifics:documentUploader.disclaimer') }}</small>
    </div>
    
    <div v-else class="mobile-uploader">
      <div class="header" v-if="!uploading">
        <div class="info-image" :class="{ big: screenParams.infoImageBig }" :style="{ backgroundImage: `url(${screenParams.infoImage})` }"></div>
        <h1>{{ i18n.t('specifics:documentUploader.header', { type: idTypeCopy || 'ID card' }) }}<span v-if="screenParams.infoModalText" class="info-btn" @click="openInfoModal"></span></h1>
        <h5>{{ i18n.t('specifics:documentUploader.info', { type: idTypeCopy }) }}</h5>
      </div>
      
      <transition name="fade">
        <button @click="openUpload" v-if="!uploading">{{ screenParams.buttonText($store) }}</button>
      </transition>


      <transition name="fade">
        <div class="uploading" v-if="uploading">
          <div class="overlay"></div>
          <div class="image-container">
            <img :src="previewURL" alt="">
          </div>
          <div class="info-container">
            <div class="fake-text"></div>
            <div class="text-uploaded" v-if="done">{{ i18n.t('specifics:documentUploader.uploaded') }}</div>
            <div class="text-uploading" v-else>
              <img class="spin" src="~static/loader_circle.svg"/>
              <p>{{ i18n.t('specifics:documentUploader.uploading') }}</p>
            </div>
            <div class="text-try-again" :class="{ invisible: !tryAgain }">{{ i18n.t('specifics:documentUploader.too_long') }} <a href="#" @click.prevent="reset">{{ i18n.t('specifics:documentUploader.try_again') }}</a></div>
          </div>
        </div>
      </transition>
      
      <transition name="fade">
        <div v-if="showInfoModal" class="info-modal">
          <p>{{ screenParams.infoModalText && screenParams.infoModalText() }}</p>
          <button class="info-modal-btn" @click="closeInfoModal">OK</button>
        </div>
      </transition>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import { mapState } from 'vuex'

let eventsToCancel = ['drag', 'dragstart', 'dragend', 'dragover', 'dragenter', 'dragleave']

export default {
  props: ['screenParams'],

  data () {
    return {
      uploading: false,
      progress: 0,
      filename: '',
      fileSize: 0,
      uploadedSize: 0,
      previewURL: '',
      done: false,
      uploadSource: null,
      showInfoModal: false,
      idType: this.$store.state.user.idType,
      tryAgain: false
    }
  },

  computed: {
    ...mapState(['app']),

    normalizedFileSize () {
      return this.normalizeSize(this.fileSize)
    },

    normalizedUploadedSize () {
      return this.normalizeSize(this.uploadedSize)
    },

    idTypeCopy () {
      const types = {
        'national_id': 'idCard',
        'passport': 'passport',
        'driver_license': 'driverLicense'
      }

      return this.i18n.t(`specifics:idType.${types[this.idType]}`)
    },

    buttonText () {
      if (this.progress) return this.i18n.t('specifics:documentUploader.uploading')
      else if (this.done) return this.i18n.t('specifics:documentUploader.done')
      else return this.screenParams.buttonText(this.$store)
    }
  },

  methods: {
    openInfoModal () {
      this.showInfoModal = true
    },

    closeInfoModal () {
      this.showInfoModal = false
    },

    reset () {
      this.uploading = false
      this.progress = 0
      this.filename = ''
      this.fileSize = 0
      this.uploadedSize = 0
      this.previewURL = ''
      this.done = false
      this.$refs.fileInput.value = ''
    },

    cancelUpload () {
      this.uploadSource.cancel()
      this.reset()
    },

    normalizeSize (size) {
      let sizes = ['B', 'KB', 'MB', 'GB']
      let index = 0

      while (size / 1024 >= 1) {
        index++
        size = size / 1024
      }

      return `${size.toFixed(1)} ${sizes[index]}`
    },

    cancelEvents (e) {
      e.preventDefault()
      e.stopPropagation()
    },

    openUpload (e) {
      if (this.uploading) return
      this.$refs.fileInput.click()
    },

    handleDrop (e) {
      this.cancelEvents(e)
      if (e.dataTransfer.files.length === 1 && /\.(jpe?g|png)$/i.test(e.dataTransfer.files[0].name)) {
        this.$refs.fileInput.files = e.dataTransfer.files
        this.startUpload()
      }
    },

    async startUpload (e) {
      if (this.$refs.fileInput.files.length === 0 || this.$refs.fileInput.files.length > 1) return
      let file = this.$refs.fileInput.files[0]

      if (file.size > 5 * 1024 * 1024) {
        this.$store.dispatch('updateApp', {
          documentUploadStatus: 'size_error'
        })

        return
      }

      this.uploading = true
      this.previewURL = URL.createObjectURL(file)
      this.filename = file.name

      let data = new FormData()
      data.append('file', file)

      try {
        let dispatchKey = this.screenParams.dispatchKey || 'updateUser'
        let propertyName = this.screenParams.propertyName || 'fileURL'

        const CancelToken = axios.CancelToken
        const source = CancelToken.source()
        this.uploadSource = source

        setTimeout(() => {
          if (!this.done) {
            this.tryAgain = true
          }
        }, 3000)

        let response = await axios.post(this.app.documentUploadUrl || 'https://httpbin.org/post', data, {
          cancelToken: this.uploadSource.token,
          onUploadProgress: (e) => {
            if (this.fileSize === 0) this.fileSize = e.total
            this.uploadedSize = e.loaded
            this.progress = Math.round((e.loaded * 100) / e.total)
          }
        })

        const uploadInfo = response.data
        if (uploadInfo.datas && uploadInfo.datas[0]) {
          let dispatchData = {}
          dispatchData[propertyName] = uploadInfo.datas[0]

          this.$store.dispatch(dispatchKey, dispatchData)
        }

        this.done = true

        setTimeout(() => {
          this.$store.dispatch('updateApp', {
            documentUploadStatus: 'success'
          })
        }, 500)

        setTimeout(() => {
          this.$store.dispatch('next', this.screenParams.nextScreenRoute)
        }, 1000)
      } catch (error) {
        this.$store.dispatch('updateApp', {
          documentUploadStatus: 'generic_error'
        })
        this.reset()
      }
    }
  },

  mounted () {
    if (this.$refs.dropZone) {
      eventsToCancel.forEach((event) => {
        this.$refs.dropZone.addEventListener(event, this.cancelEvents)
      })

      this.$refs.dropZone.addEventListener('drop', this.handleDrop)
    }
  },

  beforeDestroy () {
    if (this.$refs.dropZone) {
      eventsToCancel.forEach((event) => {
        this.$refs.dropZone.removeEventListener(event, this.cancelEvents)
      })

      this.$refs.dropZone.removeEventListener('drop', this.handleDrop)
    }
  }
}
</script>

<style lang="scss" scoped>
  @import "../assets/mixins";

  @keyframes rotate {
    75% {
      transform:rotate(275deg);
    }
    25% {
      transform:rotate(-325deg);
    }
  }

  .document-uploader {
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    margin-top: 20px;
    position: relative;

    .uploading {
      position: absolute;
      width: calc(100% + 60px);
      height: calc(100% + 80px);
      left: -30px;
      top: -50px;

      .spin {
        height: 88px;
        width: 88px;
        animation: rotate 4s infinite;
      }

      .invisible {
        visibility: hidden;
      }

      .image-container {
        display: flex;
        align-items: center;
        height: 100%;
        img { 
          width: 100%;
        }
      }

      .info-container {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        display: flex;
        flex-direction: column;
        align-items: center;
        color: #ffffff;
        text-align: center;
        justify-content: space-between;
        z-index: 20;

        .fake-text {
          height: 1px;
          visibility: hidden;
        }

        .text-uploaded,
        .text-uploading {
          font-size: 22px;
          font-weight: bold;
          position: relative;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding-top: 118px;
        }

        .text-uploaded {
          &::before {
            content: '';
            background-image: url('~static/checkmark-doc-upload.svg');
            background-repeat: no-repeat;
            position: absolute;
            top: 0;
            width: 88px;
            height: 88px;
          }
        }

        .text-uploading {
          img {
            position: absolute;
            top: 0px;
          }
        }

        .text-try-again {
          font-size: 16px;
          font-weight: 500;
          letter-spacing: 0;
          line-height: 22px;
          margin-bottom: 30px;
          a {
            color: #ffffff;
          }
        }
      }

      .overlay {
        background-color: rgba(#04040F, .5);
        position: absolute;
        height: 100%;
        width: 100%;
        z-index: 20;
      }
    }

    .info-modal {
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      background: #FFFFFF;
      position: absolute;
      top: 0;
      right: 0;
      bottom: 0;
      left: 0;
      margin-top: -50px;
      z-index: 20;
      padding: 30px;

      p {
        display: flex;
        flex: 1;
        align-items: center;
        line-height: 1.625rem;
      }

      .info-modal-btn {
        width: 100%;
        height: 60px;
        background: #EB713A;
        color: #FFFFFF;
        border-radius: 0;
        box-shadow: none;
        font-weight: 600;
        border-radius: 4px;
        text-transform: uppercase;
        font-size: 18px;
      }
    }

    .mobile-uploader {
      height: 100%;
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      position: relative;

      .header {
        display: flex;
        align-items: center;
        padding: 0 15px;
        line-height: 2rem;
        flex-direction: column;
  
        .info-image {
          width: 150px;
          height: 100px;
          background-repeat: no-repeat;
          background-size: contain;
          background-position: center;
          margin: 120px 0 60px;
        }
  
        @include desktop {
          margin-top: 30px;
          height: 100px;
        }
  
        h5 {
          font-size: 1rem;
          color: #565656;
          line-height: 22px;
          font-weight: $lightFontWeight;
          text-align: center;
          white-space: pre-line;
        }
  
        h1 {
          font-size: 22px;
          font-weight: 700;
          color: #1A212C;
          text-align: center;
          width: 100%;
          margin-bottom: 5px;
  
          .info-btn {
            display: inline-block;
            width: 20px;
            height: 20px;
            background: url(~static/info-icon.png);
            background-repeat: no-repeat;
            background-size: 100%;
            background-position: center;
            border-radius: 50%;
            opacity: 0.6;
            margin-bottom: -2px;
            cursor: pointer;
          }
        }
      }

      button {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 100%;
        height: 60px;
        background-color: $buttonPrimaryBackgroundColor;
        color: $buttonPrimaryColor;
        font-weight: 600;
        border-radius: 0;
        box-shadow: none;
        outline: none;
        position: relative;
        transition: background .2s ease;
        cursor: pointer;
        border-radius: 30px;
        overflow: hidden;
        font-size: 20px;
      }
    }

    .desktop-uploader {
      max-width: 380px;
      width: 100%;
      position: relative;

      h1 {
        color: #1A212C;
        font-size: 25px;
        font-weight: $boldFontWeight;
        letter-spacing: 0;
        line-height: 32px;
        margin-bottom: 10px;
      }

      h5 {
        height: 20px;
        color: $inputHintColor;
        font-size: 1rem;
        font-weight: $lightFontWeight;
        letter-spacing: 0;
        line-height: 20px;
      }
    
      .drop-zone {
        box-sizing: border-box;
        height: 259px;
        width: 100%;
        border: 1px dashed $inputHintColor;
        border-radius: 16px;
        background-color: #EFF2F4;
        margin-top: 40px;
        display: flex;
        flex-direction: column;
        align-items: center;

        .close {
          top: 20px;
          right: 10px;
          height: 20px;
          width: 20px;
        }

        .info,
        .upload-info {
          flex: 1;
          width: 100%;
          display: flex;
          flex-direction: column;
          align-items: center;
        }

        .info {

          .info-image {
            height: 60px;
            width: 90px;
            background-repeat: no-repeat;
            background-size: contain;
            background-position: center;
            margin-top: 40px;
          }

          .info-description {
            font-size: 14px;
            color: #A6A6A6;
            margin-top: 20px;
            font-weight: $lightFontWeight;
          }
        }

        button {
          border-radius: $buttonBorderRadius;
          height: 60px;
          max-width: 173px;
          width: 100%;
          margin: auto 0 34px;
          background-color: $buttonPrimaryBackgroundColor;
          color: $buttonPrimaryColor;
          overflow: hidden;
          position: relative;
          
          span {
            position: relative;
          }

          .progress {
            width: 100%;
            height: 100%;
            position: absolute;
            background: #9366da;
            right: 0;
            top: 0;
            bottom: 0;
          }

          &.done {
            background: $savingInputColor;

            span {
              display: none;
            }
         
            &:after {
              content: '';
              width: 19px;
              height: 14px;
              opacity: 1;
              background-image: url('~static/check-arrow.svg');
              background-size: 100%;
              margin: auto;
              position: absolute;
              left: 0;
              right: 0;
              top: 0;
              bottom: 0;
            }
          }
          
          &.loading,
          &.done {
            pointer-events: none;
          }
        }

        .upload-info {
          margin-bottom: 10px;

          .upload-info-image {
            position: relative;
            width: 100px;
            height: 100px;
            border-radius: 50%;
            border: 1px solid #FAFAFA;
            box-shadow: 0 0 0px 2px rgba(#000000, 0.15);
            padding: 2px;
            background-repeat: no-repeat;
            background-size: cover;
            background-position: center;
            overflow: hidden;

            &::after {
              content: '';
              position: absolute;
              top: 0;
              right: 0;
              bottom: 0;
              left: 0;
              background: rgba(#000000, 0.5);
              opacity: 1;
              transition: opacity .2s ease;
            }

            &.done {

              &::after {
                opacity: 0;
              }
            }
          }

          .upload-info-percentage {
            font-size: 30px;
            color: $blackTextColor;
            margin-top: 44px;
            font-weight: bold;
          }

          .upload-info-filename {
            font-size: 18px;
            color: $blackTextColor;
            font-weight: $boldFontWeight;
            text-align: center;
            margin-top: 20px;
          }

          .upload-info-sizeuploaded {
            font-size: 14px;
            color: $inputHintColor;
            margin-top: 10px;
            font-weight: $lightFontWeight;
          }
        }
      }

      .disclaimer {
        margin-top: 20px;
        display: block;
        color: $inputHintColor;
        font-size: 12px;
        font-weight: $lightFontWeight;
        letter-spacing: 0;
        line-height: 15px;
        display: flex;
        align-content: center;
        
        &:before {
          content: '';
          height: 14px;
          width: 11px;
          background: url('~static/lock.svg') no-repeat;
          background-size: contain;
          display: inline-block;
          margin-right: 6px;
        }

        @include desktop-big {
          margin-top: 50px;
        }
      }
    }
  }

  .file-input {
    visibility: hidden;
    position: absolute;
  }
</style>


// WEBPACK FOOTER //
// src/screens/DocumentUploader.vue